// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 65.19.0 — legal matters (toolkit row LEG-01).
 *
 * ## The distinction this file exists to keep
 *
 * A matter raised under a right the law grants and a matter the organisation
 * elected to track are not the same thing, and a reader who cannot tell them
 * apart cannot tell an obligation from a practice. {@link LegalMatterOrigin} is
 * that distinction in the type, so it survives into the screen, the export and
 * what the advisor may say — rather than living in a comment.
 *
 * `VN_PDPL_LAW_2025` declares `complaint` (khiếu nại) and `denunciation`
 * (tố cáo) in its `rights` block, both at **Art. 4(1)(đ)**, and the platform had
 * no surface that received either. That is the gap this module closes.
 *
 * ## What is NOT claimed
 *
 * A declared data-subject right is not a controller duty with a requirement
 * behind it, and no requirement in the installed pack states one.
 * [65.16.1](../../../../docs/product/phases/phase-28/sprint-65.16.1.md) read the
 * signed text and found that Decree 356 Điều 36(6) assigns *"giải quyết khiếu
 * nại, tố cáo"* to the Ministry while Law 91 Điều 4(1)(đ) defers to other law —
 * so **no instrument obliges the controller to operate an intake**, and the row
 * is elective in full, as the owner ruled.
 *
 * The origin therefore records *what the person exercising it invoked*, not a
 * duty the platform asserts. Both halves are opt-in; what differs is what a
 * matter RESTS ON, and an operator reading a register of matters needs that
 * even when neither half is compulsory.
 *
 * ## The platform derives no legal position
 *
 * No exposure figure, no likelihood, no advice, no outcome. It records what was
 * received, what was done, and what a person concluded. Every judgement field
 * on these records is written by a named person or is absent.
 */
/**
 * What a matter rests on.
 *
 * `statutory_right` is reserved for matters a data subject raises by invoking a
 * right an installed instrument declares — the platform does not decide this,
 * the person recording the matter does, and {@link LegalMatter.right_code}
 * names which right. Everything else is `elective`.
 */
export const LEGAL_MATTER_ORIGINS = ['statutory_right', 'elective'];
/**
 * What kind of matter this is.
 *
 * `complaint` and `denunciation` are kept apart rather than folded into one
 * "grievance": Vietnamese law treats khiếu nại and tố cáo as different acts with
 * different subjects — one contests a decision affecting the person making it,
 * the other reports a violation which may affect somebody else — and a register
 * that collapsed them would lose which was raised.
 */
export const LEGAL_MATTER_KINDS = [
    'complaint',
    'denunciation',
    'claim',
    'litigation',
    'regulatory_penalty',
    'other',
];
/**
 * Where a matter has got to.
 *
 * `acknowledged` is its own state and not a flavour of `received`: telling the
 * person who raised it that you have it is an act with a date, and a register
 * that could not show the gap between the two could not show a delay.
 *
 * `withdrawn` is its own outcome and not a flavour of `resolved`: a matter the
 * raiser took back is not one the organisation concluded, and recording it as
 * resolved would ask for a conclusion nobody reached. It is deliberately not a
 * witness on the closing fact, for the same reason.
 *
 * There is no `under_review`. It was declared and then removed rather than
 * shipped: `acknowledged` already says the organisation has it, and without an
 * owner an intermediate state says nothing more. `union-reachability` refused
 * it as written by no route-reachable path, which was the correct answer — a
 * state nobody can set is vocabulary the product does not have.
 */
export const LEGAL_MATTER_STATES = ['received', 'acknowledged', 'resolved', 'withdrawn'];
/** Who raised it. A role rather than a classification the platform derives. */
export const LEGAL_MATTER_RAISED_BY = [
    'data_subject',
    'employee',
    'regulator',
    'counterparty',
    'other',
];
/** How many suggested links one matter records at most (P3-L2 / P3-N5). */
export const LEGAL_MATTER_MAX_SUGGESTED_LINKS = 5;
/** A person's decision on a suggested link. `null` until one is recorded. */
export const LEGAL_MATTER_SUGGESTED_LINK_DECISIONS = ['accepted', 'dismissed'];
//# sourceMappingURL=legal-matters.js.map